// frontend/admin-portal/src/pages/ImportPurchaseOrder.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Input,
  Alert,
  Table,
  FileUploader,
  Badge,
  Spinner,
} from "@fiberhome/shared-ui";
import { purchaseOrderService } from "../services/purchaseOrderService";
import { supplierService } from "../services/supplierService";

export default function ImportPurchaseOrder() {
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [form, setForm] = useState({
    supplierId: "",
    currency: "USD",
    shipping: 0,
    expectedAt: "",
    paymentTerms: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    supplierService
      .list({ isActive: true, limit: 200 })
      .then((d) => setSuppliers(d.items || d))
      .catch(() => setSuppliers([]));
  }, []);

  const handleDownloadTemplate = async () => {
    try {
      await purchaseOrderService.downloadImportTemplate();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Download failed");
    }
  };

  const handleFile = async (files) => {
    const f = files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setError(
        "Please save your Excel file as CSV (comma-separated) and upload again.",
      );
      return;
    }
    setFile(f);
    setPreview(null);
    setMapping(null);
    setResult(null);
    setError("");
    setBusy(true);
    try {
      const data = await purchaseOrderService.importPreview(f);
      setPreview(data);
      setMapping(data.suggestedMapping);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Preview failed");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const setMap = (key, col) =>
    setMapping((m) => ({ ...m, [key]: col || null }));

  const handleConfirm = async () => {
    if (!form.supplierId) {
      setError("Select a supplier first");
      return;
    }
    if (!mapping?.name || !mapping?.quantity || !mapping?.unitPrice) {
      setError("Map name, quantity, and unitPrice columns");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await purchaseOrderService.importConfirm({
        supplierId: Number(form.supplierId),
        mapping,
        rows: preview.rawRows,
        currency: form.currency,
        shipping: Number(form.shipping) || 0,
        expectedAt: form.expectedAt || null,
        paymentTerms: form.paymentTerms || null,
        notes: form.notes || null,
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const headerOptions = preview?.headers || [];

  return (
    <>
      <PageHeader
        title="Import Purchase Order"
        subtitle="Upload a CSV from your supplier to create a PO directly"
        breadcrumbs={["Home", "Procurement", "Purchase Orders", "Import"]}
        actions={
          <div className="d-flex gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={busy}
            >
              Download Template
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/purchase-orders")}
            >
              Back to POs
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {!preview && (
        <Card title="Step 1 — Choose supplier and upload CSV" className="mb-3">
          <Alert variant="info" className="mb-3">
            <strong>Need a template?</strong> Click <em>Download Template</em>{" "}
            above to get a prefilled CSV with the correct column names and 5
            example rows. Save your supplier's offer sheet as a CSV in the same
            format, then upload it below.
          </Alert>

          <label className="fh-input-label">Supplier *</label>
          <select
            className="fh-input mb-3"
            value={form.supplierId}
            onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
          >
            <option value="">Select a supplier</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <FileUploader
            accept=".csv,text/csv"
            multiple={false}
            maxSizeMB={5}
            onFiles={handleFile}
            label="Choose CSV file"
            dragLabel="Drag & drop your supplier's CSV here or click to browse"
          />

          {busy && (
            <div className="d-flex align-items-center gap-2 mt-3">
              <Spinner size="sm" />
              <span className="text-muted">Parsing {file?.name}…</span>
            </div>
          )}
        </Card>
      )}

      {preview && !result && (
        <>
          <Card title="Step 2 — Map columns" className="mb-3">
            <Alert variant="info" className="mb-3">
              Detected <strong>{preview.totalRows}</strong> rows and{" "}
              <strong>{preview.headers.length}</strong> columns. Confirm which
              columns hold the product name, quantity, and unit price.
            </Alert>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="fh-input-label">Product Name column *</label>
                <select
                  className="fh-input"
                  value={mapping?.name || ""}
                  onChange={(e) => setMap("name", e.target.value)}
                >
                  <option value="">—</option>
                  {headerOptions.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="fh-input-label">Quantity column *</label>
                <select
                  className="fh-input"
                  value={mapping?.quantity || ""}
                  onChange={(e) => setMap("quantity", e.target.value)}
                >
                  <option value="">—</option>
                  {headerOptions.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="fh-input-label">Unit Price column *</label>
                <select
                  className="fh-input"
                  value={mapping?.unitPrice || ""}
                  onChange={(e) => setMap("unitPrice", e.target.value)}
                >
                  <option value="">—</option>
                  {headerOptions.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="fh-input-label">Unit column</label>
                <select
                  className="fh-input"
                  value={mapping?.unit || ""}
                  onChange={(e) => setMap("unit", e.target.value)}
                >
                  <option value="">(default: pcs)</option>
                  {headerOptions.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="fh-input-label">SKU column</label>
                <select
                  className="fh-input"
                  value={mapping?.sku || ""}
                  onChange={(e) => setMap("sku", e.target.value)}
                >
                  <option value="">(auto-generate)</option>
                  {headerOptions.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="fh-input-label">Notes column</label>
                <select
                  className="fh-input"
                  value={mapping?.notes || ""}
                  onChange={(e) => setMap("notes", e.target.value)}
                >
                  <option value="">(none)</option>
                  {headerOptions.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          <Card title="Preview (first 50 rows)" className="mb-3">
            <Table
              columns={[
                { key: "lineNumber", label: "#", width: 40 },
                { key: "sku", label: "SKU" },
                { key: "name", label: "Name" },
                { key: "quantity", label: "Qty" },
                { key: "unit", label: "Unit" },
                {
                  key: "unitPrice",
                  label: "Unit Price",
                  render: (r) => Number(r.unitPrice).toFixed(2),
                },
                {
                  key: "lineTotal",
                  label: "Total",
                  render: (r) => (
                    <strong>{Number(r.lineTotal).toFixed(2)}</strong>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (r) => (
                    <Badge variant={r.status === "ok" ? "success" : "danger"}>
                      {r.status}
                    </Badge>
                  ),
                },
              ]}
              data={preview.previewRows}
              emptyMessage="No rows"
            />
            {preview.truncated && (
              <Alert variant="info" className="mt-2">
                Showing first 500 rows for preview. All rows will be imported on
                confirm.
              </Alert>
            )}
          </Card>

          <Card title="Step 3 — PO details and confirm" className="mb-3">
            <div className="row g-3">
              <div className="col-md-4">
                <Input
                  label="Currency"
                  value={form.currency}
                  onChange={(e) =>
                    setForm({ ...form, currency: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <Input
                  label="Shipping"
                  type="number"
                  value={form.shipping}
                  onChange={(e) =>
                    setForm({ ...form, shipping: e.target.value })
                  }
                />
              </div>
              <div className="col-md-4">
                <Input
                  label="Expected Date"
                  type="date"
                  value={form.expectedAt}
                  onChange={(e) =>
                    setForm({ ...form, expectedAt: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <Input
                  label="Payment Terms"
                  value={form.paymentTerms}
                  onChange={(e) =>
                    setForm({ ...form, paymentTerms: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <Input
                  label="Notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setPreview(null);
                  setFile(null);
                  setMapping(null);
                }}
                disabled={busy}
              >
                Back
              </Button>
              <Button variant="primary" onClick={handleConfirm} loading={busy}>
                Import{" "}
                {preview.previewRows.filter((r) => r.status === "ok").length}+
                rows
              </Button>
            </div>
          </Card>
        </>
      )}

      {result && (
        <Card title="Purchase Order Imported">
          <Alert variant="success" className="mb-3">
            Created <strong>{result.purchaseOrder.code}</strong> with{" "}
            {result.summary.imported} line
            {result.summary.imported === 1 ? "" : "s"} — subtotal{" "}
            {result.purchaseOrder.currency}{" "}
            {Number(result.summary.subtotal).toLocaleString()}
          </Alert>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={() =>
                navigate(`/purchase-orders/${result.purchaseOrder.id}`)
              }
            >
              Open PO
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setPreview(null);
                setFile(null);
                setResult(null);
              }}
            >
              Import another file
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/purchase-orders")}
            >
              All POs
            </Button>
          </div>
        </Card>
      )}
    </>
  );
}
